import React from 'react';
import { useTranslation } from 'react-i18next';

import Loading from '../Common/Loading';

// Actions
import { getMarketingPermissions, patchMarketingPermissions } from '../../actions';
// Types
import { CustomerId, MarketingPermissions } from '../../types/customer';
// Utils.
import { gaEventIfAllowed } from '../../utils/googleAnalytics';
import Button from '../Common/Button';

const CustomerCommunicationChoices: React.FunctionComponent<CustomerId> = (props: CustomerId) => {
  const [currentUserPermissions, setCurrentUserPermissions] = React.useState<MarketingPermissions>();

  const [marketingEmail, setMarketingEmail] = React.useState<boolean>(currentUserPermissions ? currentUserPermissions.marketingEmail : false);
  const [marketingSMS, setMarketingSMS] = React.useState<boolean>(currentUserPermissions ? currentUserPermissions.marketingSMS : false);
  const [marketingNewsLetter, setMarketingNewsLetter] = React.useState<boolean>(
    currentUserPermissions ? currentUserPermissions.marketingNewsLetter : false,
  );

  const [updateMessage, setUpdateMessage] = React.useState<string>('');
  const [updating, setUpdating] = React.useState<boolean>(false);
  const { t } = useTranslation();
  const currentUserId = props.userId;

  React.useEffect(() => {
    async function fetchMarketingPermissions(): Promise<void> {
      const permissions = await getMarketingPermissions(currentUserId);
      setCurrentUserPermissions(permissions);
      setMarketingEmail(permissions.marketingEmail);
      setMarketingSMS(permissions.marketingSMS);
      setMarketingNewsLetter(permissions.marketingNewsLetter);
    }
    fetchMarketingPermissions();
  }, [currentUserId]);

  const submitForm = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setUpdateMessage('');
    setUpdating(true);

    gaEventIfAllowed({
      category: 'Omien tietojen päivittäminen',
      action: 'Päivitä markkinointiluvat',
    });

    async function updateMarketingPermissions(): Promise<void> {
      const status = await patchMarketingPermissions(currentUserId, marketingNewsLetter, marketingEmail, marketingSMS);

      if (status === 204) {
        setUpdateMessage(`${t('myInfo:contact.myInfoUpdated')}`);
        setUpdating(false);
      } else if (status === 404) {
        setUpdateMessage(`${t('myInfo:contact.myInfoUserNotFound')}`);
        setUpdating(false);
      } else {
        setUpdating(false);
      }
    }
    updateMarketingPermissions();
  };

  return (
    <div className="customer-communications">
      <h4 className="h3">{t('myInfo:contact.contactingInfo')}</h4>

      <p>
        <strong>{t(`myInfo:contact.discountMessages${new Date().getFullYear() >= 2026 ? '2026' : ''}`)}</strong>
      </p>

      <form onSubmit={(e): void => submitForm(e)}>
        <div className="customer-communication-button-row">
          <div className="form-item checkbox">
            <input type="checkbox" id="email" onChange={(): void => setMarketingEmail(!marketingEmail)} checked={marketingEmail} />
            <div className="form-item-input"></div>
            <label htmlFor="email">{t('myInfo:messageChoices.email')}</label>
          </div>

          <div className="form-item checkbox">
            <input type="checkbox" id="sms-message" onChange={(): void => setMarketingSMS(!marketingSMS)} checked={marketingSMS} />
            <div className="form-item-input"></div>
            <label htmlFor="sms-message">{t('myInfo:messageChoices.smsMessage')}</label>
          </div>
        </div>

        <p>
          <strong>{t('myInfo:contact.customerMagazine')}</strong>
        </p>

        <div className="customer-communication-button-row">
          <div className="form-item checkbox">
            <input
              type="checkbox"
              id="magazineYes"
              onChange={(): void => setMarketingNewsLetter(!marketingNewsLetter)}
              checked={marketingNewsLetter}
            />
            <div className="form-item-input"></div>
            <label htmlFor="magazineYes">{t('myInfo:messageChoices.magazineYes')}</label>
          </div>
        </div>

        <Button
          type="submit"
          disabled={updating ? true : false}
          onClick={(e): void => {
            // @ts-ignore
            if (e.target.ariaDisabled !== 'false') {
              e.preventDefault();
            }
          }}
        >
          {t('myInfo:messageChoices.updateUserInfo')}
        </Button>
        {updating && <Loading size="small" />}

        {updateMessage !== '' && <p aria-live="polite">{updateMessage}</p>}
      </form>
    </div>
  );
};

export default CustomerCommunicationChoices;
